import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import { PlatformQueryService } from '@core/services/platform-query.service';
import { PlatformQuery } from '@shared/models/platform-query/platform-query';
import {take, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {Api} from "@shared/constants/common-field.constants";
import {DataTracingService} from "@features/data-tracing/services/data-tracing.service";
import { List } from 'yfiles';
import {FlexSheetService} from "@features/spreadsheet/services/flex-sheet.service";
import {ActivityRightSidebarStateService} from "@features/data-tracing/services/activity-right-sidebar-state.service";
import {ActivityComponents} from "@features/data-tracing/constants/activity-components";
import {SocketClusterService} from "@core/services/socket-cluster.service";
import { DataTracingPageDto, generateTracingKey, EnhancedDataTracingRecordMap, IDataSource, IDataTracingEnhancedRecord, IDataTracingResponse } from '@features/data-tracing/models/data-tracing-v2';

@Component({
    standalone: false,
    selector: 'app-latest-data-sources',
  templateUrl: './latest-data-sources.component.html',
  styleUrls: ['./latest-data-sources.component.scss']
})
export class LatestDataSourcesComponent implements OnInit, OnDestroy , OnChanges{
  @Input() public invalidate: boolean;
  @Input() public data: any;
  @Output() public dataSourcesEvent: EventEmitter<any> = new EventEmitter<any>();

  public dataEditSummaryId: string;
  public sourceType: string;

  dataTracingResponse: IDataTracingResponse = null;

  dataTracingPages: {[key:string]: DataTracingPageDto[]} = {};
  dataTracingColumnRecords: EnhancedDataTracingRecordMap = {};
  dataTracingRowRecords: EnhancedDataTracingRecordMap  = {};
  isDataReceived: boolean = false;


  private readonly rowPageSize: number = 10;
  private readonly columnPageSize: number = 20;


  // Data Tracing Column Pagination Info
  dataTracingColumnPageNo: number = 0;
  dataTracingColumnPageSize: number = this.columnPageSize;
  dataTracingColumnPageInfo : {[key:string]: number} = {};
  enableShowMoreInfoColumn: {[key:string]: boolean} = {};

  // Data Tracing Row Pagination Info
  dataTracingRowPageNo: number = 0;
  dataTracingRowPageSize: number = this.rowPageSize
  dataTracingRowPageInfo : {[key:string]: number} = {};
  enableShowMoreInfoRow: {[key:string]: boolean} = {};

  private noRecord: {[key: string]: boolean} = {};


  unSubscribe$ = new Subject<void>();

  constructor(
    private dataTracingService: DataTracingService,
    private activityRightSidebarStateService: ActivityRightSidebarStateService,
    private flexSheetService: FlexSheetService,
    private platformQueryService: PlatformQueryService) {
  }

  private clearResponseData() {
    this.dataTracingResponse = null;


    //Column
    this.dataTracingColumnRecords = {};
    this.dataTracingColumnPageInfo = {};
    this.dataTracingColumnPageNo = 0;
    this.dataTracingColumnPageSize = this.columnPageSize;
    this.enableShowMoreInfoColumn = {};


    //Row 
    this.dataTracingRowRecords = {};
    this.dataTracingRowPageInfo = {};
    this.dataTracingRowPageSize = this.rowPageSize;
    this.dataTracingRowPageNo = 0;
    this.enableShowMoreInfoRow = {};

  }

  ngOnInit(): void {
    this.activityRightSidebarStateService.setCurrentActivityRightSidebarComponent(ActivityComponents.DataSourceComponent);
    this.setCurrentDataEditSummaryId();
    console.log('LatestDataSourcesComponent onInit', this.dataEditSummaryId, this.sourceType);
    this.getDataTracing();
  }
  private getDataTracing() {
    console.log('LatestDataSourcesComponent getDataTracing', this.dataEditSummaryId, this.sourceType);

    if(this.isDataReceived)return;

    if (!this.dataEditSummaryId && !this.sourceType) {
      this.isDataReceived = true;
      return;
    }
    this.clearResponseData();
    const query = new PlatformQuery();
    query.api = Api.Collaboration;
    query.name = 'DataTracingChartQuery';
    query.projectId = '[PROJECT_ID]';
    query.setValue('DataTracingDocumentId', this.dataEditSummaryId);
    query.setValue('SourceType', this.sourceType);
    this.tryAddScenarioInformationToQuery(query);

    console.log('DataTracing Chart Query', query);

    this.isDataReceived = false;

    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      console.log('DataTracingResponse:', response);
      this.dataTracingResponse = response.fieldValues['DataTracingResponse'];
      if (!this.dataTracingResponse?.dataSources?.length) {
        this.dataTracingResponse = null;
      }
      if (this.dataTracingResponse) {
        this.dataSourcesEvent.emit({
          type: 'DataTracingResponseReceived',
          data : {
            'DataEditSummaryId': this.dataTracingResponse.dataEditSummaryId,
            'DataEditSummaryTitle': this.dataTracingResponse.summaryTitle
          }
        });
      }
      this.isDataReceived = true;
    });
  }

  // ON Expand source populate data tracing page
 onExpandSource(dataSource) {
    console.log('onExpandSource', dataSource);
    if (this.dataTracingPages[dataSource.dataEditSourceType]) return;
    this.getDataTracingPages(dataSource);
  }

  // On Expand page populate the column data
  public onExpandPage(dataSource: IDataSource, dataTracingPageDto: DataTracingPageDto): void {
    console.log('onExpandPage', dataSource, dataTracingPageDto);

    var key = generateTracingKey(dataSource, dataTracingPageDto.key);

    if(this.dataTracingColumnRecords[key])return;

    //page row info for the key
    this.dataTracingColumnPageInfo[key] = this.dataTracingColumnPageNo + 1;

    //show more for the key
    this.enableShowMoreInfoColumn[key] = true;

    this.getDataTracinColumnRecords(dataSource, dataTracingPageDto, key);
  }

  //On expand column populate the row data
  public onExpandColumn(dataSource : IDataSource, dataTracingPage : DataTracingPageDto, dataTracingPageRecord: IDataTracingEnhancedRecord): void
  {
    var key = generateTracingKey(dataSource, dataTracingPageRecord.columnIndex);
    
    if(this.dataTracingRowRecords[key]) return;
    
    //page row info for the key
    this.dataTracingRowPageInfo[key] = this.dataTracingRowPageNo + 1;

    //show more for the key
    this.enableShowMoreInfoRow[key] = true;

    this.getDataTracingRowRecords(dataSource, dataTracingPage, dataTracingPageRecord, key)
  }


  onClickRecord(dataSource, dataTracingPage, dataTracingRowRecord) {
    console.log('onClickRecord', dataSource, dataTracingPage, dataTracingRowRecord);
    // need to show the field history details
    this.dataSourcesEvent.emit({
      type: 'RecordClicked',
      data: {
        'ReferenceCode' : dataTracingPage.key, 
        'CellRowIndex': dataTracingRowRecord.rowIndex,
        'CellColumnIndex': dataTracingRowRecord.columnIndex,
        'SourceType': dataSource.dataEditSourceType,
        'DestinationType' : this.dataTracingResponse.destinationType
      }
    });
  }

  showNextRowData(dataSource: IDataSource, dataTracingPage: DataTracingPageDto, dataTracingPageRecord: IDataTracingEnhancedRecord): void
  {
    var key = generateTracingKey(dataSource, dataTracingPageRecord.columnIndex);
    this.dataTracingRowPageInfo[key] += 1;
    this.getDataTracingRowRecords(dataSource, dataTracingPage, dataTracingPageRecord, key);
  }

  showNextColumnData(dataSource: IDataSource, dataTracingPageDto: DataTracingPageDto): void
  {
    var key = generateTracingKey(dataSource, dataTracingPageDto.key);
    this.dataTracingColumnPageInfo[key] += 1;
    this.getDataTracinColumnRecords(dataSource, dataTracingPageDto, key);
  }

  

  private getDataTracingPages(dataSource) {
    const query = new PlatformQuery();
    query.api = Api.Collaboration;
    query.name = 'DataTracingPageQuery';
    query.projectId = '[PROJECT_ID]';
    query.fieldValues = {
      'DataTracingDocumentId': this.dataEditSummaryId,
      'SourceType': this.sourceType,
      'DataEditSourceType': dataSource.dataEditSourceType,
      'DataEditDestinationType': this.dataTracingResponse.destinationType
    };
    this.tryAddScenarioInformationToQuery(query);
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      console.log('DataTracingPageQuery:', response);
      const dataTracingPages = response.fieldValues['DataTracingPages'];
      if (dataTracingPages) {
        this.dataTracingPages[dataSource.dataEditSourceType] = dataTracingPages;
        console.log(this.dataTracingPages);
      }
    });
  }



  private getDataTracinColumnRecords(dataSource: IDataSource, dataTracingPageDto: DataTracingPageDto, key: string): void {

    // create the query
    const query = new PlatformQuery();
    query.api = Api.Collaboration;
    query.name = 'DataTracingPageRecordsQuery';
    query.projectId = '[PROJECT_ID]';
    query.fieldValues = {
      'DataTracingDocumentId': this.dataEditSummaryId,
      'DataTracingPageId': dataTracingPageDto.key,
      'SourceType': this.sourceType,
      'DataEditSourceType': dataSource.dataEditSourceType,
      'DataEditDestinationType': this.dataTracingResponse.destinationType,
      'PageNo': this.dataTracingColumnPageInfo[key], //this.pageNo,
      'PageSize': this.dataTracingColumnPageSize
    };

    this.tryAddScenarioInformationToQuery(query);

    //execute
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {

      console.log('DataTracingRecordsQueryResponse:', response);

      if (response.fieldValues['DataTracingPageRecords']) {

        let records: IDataTracingEnhancedRecord[] = response.fieldValues['DataTracingPageRecords'];

        this.enableShowMoreInfoColumn[key] = response.fieldValues['RecordsCount'] == this.dataTracingColumnPageSize;

        if(this.dataTracingColumnPageInfo[key] > 1){

          this.dataTracingColumnRecords[key] = this.dataTracingColumnRecords[key].concat(records);

        } else {
          this.dataTracingColumnRecords[key] = records
        }
      }
    });
  }

  private getDataTracingRowRecords(dataSource: IDataSource, dataTracingPage: DataTracingPageDto, dataTracingPageRecord: IDataTracingEnhancedRecord, key: string): void
  {
    // create the query
    const query = new PlatformQuery();
    query.api = Api.Collaboration;
    query.name = 'DataTracingRowRecordsQuery';
    query.projectId = '[PROJECT_ID]';
    query.fieldValues = {
      'DataTracingDocumentId': this.dataEditSummaryId,
      'DataTracingPageId': dataTracingPage.key,
      'SourceType': this.sourceType,
      'DataEditSourceType': dataSource.dataEditSourceType,
      'DataEditDestinationType': this.dataTracingResponse.destinationType,
      'PageNo': this.dataTracingRowPageInfo[key], //this.pageNo,
      'PageSize': this.dataTracingRowPageSize,
      'ColumnIndex': dataTracingPageRecord.columnIndex
    };
    this.tryAddScenarioInformationToQuery(query);

    //execute
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {

      console.log('DataTracingRowRecords query response:', response);

      if (response.fieldValues['DataTracingRowRecords']) {

        let rowRecords: IDataTracingEnhancedRecord[] = response.fieldValues['DataTracingRowRecords'];

        this.enableShowMoreInfoRow[key] = response.fieldValues['RecordsCount'] == this.dataTracingRowPageSize;

        if(this.dataTracingRowPageInfo[key] > 1)
        {
          this.dataTracingRowRecords[key] = this.dataTracingRowRecords[key].concat(rowRecords);
        }
        else{
          this.dataTracingRowRecords[key] = rowRecords
          if(rowRecords.length == 0)this.noRecord[key] = true;
        }

        // console.log(this.dataTracingColumnRecords);
      }

    });
  }

  showNoRecord(key: string): boolean
  {
    return this.noRecord[key];
  }

  private tryAddScenarioInformationToQuery(query: PlatformQuery) : boolean {
    if (this.platformQueryService.platformLinkModel && this.platformQueryService.platformLinkModel.fieldValues['ScenarioId']) {
      query.setValue("ScenarioId", this.platformQueryService.platformLinkModel.fieldValues['ScenarioId']);
      query.setValue("CountryCode", this.platformQueryService.platformLinkModel.fieldValues['CountryCode']);
      query.setValue("ScenarioType", this.platformQueryService.platformLinkModel.fieldValues['ScenarioType']);
      return true;
    }
    return false;
  }

  ngOnDestroy(): void {
    this.activityRightSidebarStateService.unloadActivityRightSidebarComponent(ActivityComponents.DataSourceComponent);
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('DataSourcesComponent Changes', changes);
    let changed = false;
    if (changes['data']?.currentValue) {
      if (this.setCurrentDataEditSummaryId()) {
        changed = true;
      }
      if (this.data?.SourceType) {
        if (this.data?.SourceType !== this.sourceType) {
          this.sourceType = this.data.SourceType;
          changed = true;
        }
      }
      if (this.data?.Invalidate) {
        changed = true;
      }
    }
    if (!changes['invalidate']?.isFirstChange() && this.invalidate) {
      changed = true
      this.invalidate = false;
    }
    if (changed) {
      // this.getDataTracingv2();
    }
  }

  setCurrentDataEditSummaryId() {
    if (this.data?.DataEditSummaryId) {
      if (this.data?.DataEditSummaryId !== this.dataEditSummaryId) {
        this.dataEditSummaryId = this.data.DataEditSummaryId;
        return true;
      }
    }
    if (this.platformQueryService.platformLinkModel?.fieldValues?.DataEditSummaryId) {
      if (this.platformQueryService.platformLinkModel?.fieldValues?.DataEditSummaryId !== this.dataEditSummaryId){
        this.dataEditSummaryId = this.platformQueryService.platformLinkModel?.fieldValues?.DataEditSummaryId
        return true;
      }
    }
    if (this.flexSheetService.getFlexSheet() && this.flexSheetService.getCurrentWorkBook()?.id) {
      if (this.flexSheetService.getCurrentWorkBook()?.id !== this.dataEditSummaryId) {
        this.dataEditSummaryId = this.flexSheetService.getCurrentWorkBook()?.id;
        return true;
      }
    }
    return false;
  }

  public getKey(dataSource: IDataSource, indexValue: string): string
  {
    return generateTracingKey(dataSource, indexValue);
  }

}
